import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Transaction, RecurringTransaction } from '@/types/transaction';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NavigationProps } from '@/types/navigation';
import { format, parseISO, isSameDay } from 'date-fns'; // For date formatting and grouping
import { Eye } from 'lucide-react'; // Import the Eye icon
import { TransactionDetailDialog } from '../TransactionDetailDialog'; // Import the dialog
import { BankAccount } from '@/types/plaid'; // Import BankAccount type

// Define UserCategorySetting interface locally or import it
interface UserCategorySetting {
  name: string;
  emoji: string;
}

interface UpcomingTransactionsCardProps {
  transactions: Transaction[] | RecurringTransaction[]; // Allow both types for now
  isLoading: boolean;
  onNavigate?: NavigationProps['onNavigate'];
  userCategorySettings: Map<string, UserCategorySetting>; 
  bankAccounts: BankAccount[];
  title?: string; // Add optional title
  description?: string; // Add optional description
}

// Helper to group regular transactions by date
/*
const groupTransactionsByDate = (transactions: Transaction[]) => {
  const grouped: { [key: string]: Transaction[] } = {};
  transactions.forEach(tx => {
    try {
        // Assuming tx.date is 'YYYY-MM-DD' string
        const dateKey = tx.date; 
        if (!grouped[dateKey]) {
            grouped[dateKey] = [];
        }
        grouped[dateKey].push(tx);
    } catch (e) {
        console.error("Error parsing date for grouping:", tx.date, e);
    }
  });
  // Sort dates ascending (oldest first)
  return Object.entries(grouped).sort(([dateA], [dateB]) => dateA.localeCompare(dateB));
};
*/

// Helper to group RECURRING transactions by NEXT date
const groupRecurringTransactionsByDate = (transactions: RecurringTransaction[]) => {
  const grouped: { [key: string]: RecurringTransaction[] } = {};
  transactions.forEach(tx => {
    try {
      const dateKey = tx.predicted_next_date; // Use predicted_next_date for grouping
      if (!dateKey || typeof dateKey !== 'string') {
        console.warn("Skipping recurring transaction with invalid predicted_next_date:", tx);
        return; 
      }
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(tx);
    } catch (e) {
      console.error("Error processing recurring transaction for grouping:", tx, e);
    }
  });
  // Sort dates ascending (oldest first)
  return Object.entries(grouped).sort(([dateA], [dateB]) => dateA.localeCompare(dateB));
};

export const UpcomingTransactionsCard: React.FC<UpcomingTransactionsCardProps> = ({ 
  transactions, 
  isLoading, 
  onNavigate, 
  userCategorySettings, // Destructure new props
  bankAccounts,
  title = "Upcoming Transactions", // Default title
  description = "Expected recurring transactions." // Default description
}) => {
  // State for the dialog
  const [viewingTransaction, setViewingTransaction] = useState<Transaction | RecurringTransaction | null>(null);

  // Re-add handleViewAll function
  const handleViewAll = () => {
    onNavigate?.('subscriptions');
  };

  // Adjust based on whether we want to show ALL upcoming or just next 5
  const itemsToShow = transactions.slice(0, 5); // Show first 5 upcoming for now
  // Group only the items to show by date (adjust date field if needed for RecurringTransaction)
  // const groupedTransactions = !isLoading ? groupTransactionsByDate(itemsToShow as Transaction[]) : []; // Cast for grouping function
  const groupedTransactions = !isLoading ? groupRecurringTransactionsByDate(itemsToShow as RecurringTransaction[]) : [];
  
  // Helper function to shorten the name
  const getShortName = (name: string | null | undefined, maxLength = 20): string => {
    if (!name) {
      return 'Unknown';
    }
    if (name.length <= maxLength) {
      return name;
    }
    // Find the last space within the maxLength
    const truncated = name.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    // If a space exists and it's not at the beginning, truncate at the space
    if (lastSpace > 0) {
      return truncated.substring(0, lastSpace) + '...';
    }
    // Otherwise, just truncate at maxLength
    return truncated + '...';
  };

  // Function to format date string 'YYYY-MM-DD' to 'Month Day, YYYY'
  const formatDisplayDate = (dateString: string): string => {
    try {
        // Parse YYYY-MM-DD. Adding time avoids timezone issues during parsing.
        const date = parseISO(dateString + 'T00:00:00'); 
        return format(date, 'MMMM do, yyyy');
    } catch (e) {
        console.error("Error formatting display date:", dateString, e);
        return dateString; // Fallback
    }
  };

  // --- Dialog Handlers ---
  const handleViewDetails = (tx: Transaction | RecurringTransaction) => {
    setViewingTransaction(tx);
  };

  const handleCloseDialog = () => {
    setViewingTransaction(null);
  };
  // --- End Dialog Handlers ---

  return (
    <Card className="shadow-lg relative w-full"> {/* Removed bg-white */}
      {/* Use default CardHeader styling */}
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle> {/* Use title prop */}
            <CardDescription>{description}</CardDescription> {/* Use description prop */}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative"> 
          {/* Timeline line with better visibility */}
          {groupedTransactions.length > 1 && (
             <div className="absolute left-3 ml-0.5 top-0 bottom-0 w-1 bg-gradient-to-b from-border via-border via-50% to-transparent "></div>
          )}

          {isLoading ? (
             // --- Loading Skeleton --- 
            <div className="space-y-8 pt-2">
                {[...Array(3)].map((_, dayIndex) => (
                    <div key={dayIndex} className="relative">
                         {/* Date marker skeleton */}
                         <div className="flex items-center mb-4 relative">
                            <Skeleton className="absolute left-4 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full" />
                            <Skeleton className="ml-10 h-5 w-1/3" />
                         </div>
                         {/* Item skeletons */}
                         <div className="ml-10 pl-2 space-y-3">
                            {[...Array(2)].map((_, itemIndex) => (
                                <div key={itemIndex} className="flex items-center justify-between p-3 border rounded-lg">
                                    <Skeleton className="h-5 w-2/5" />
                                    <Skeleton className="h-5 w-1/5" />
                                </div>
                            ))}
                         </div>
                    </div>
                ))}
            </div>
          ) : groupedTransactions.length === 0 ? (
             // --- Empty State --- 
             <p className="text-sm text-muted-foreground text-center py-10">No recent activity found.</p>
          ) : (
            // --- Purchase items --- 
            <div className="space-y-6">
              {groupedTransactions.map(([dateKey, dayTransactions], dayIndex) => (
                <div key={dayIndex} className="relative">
                  {/* Date marker with better visibility */}
                  <div className="flex items-center mb-4 relative">
                    <div className="absolute left-4 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-card border-2 border-primary/20 ring-background"></div>
                    <div className="ml-10 font-medium text-foreground text-sm">{formatDisplayDate(dateKey)}</div>
                  </div>
                  
                  {/* Purchase details */} 
                  <div className="ml-10 pl-2 space-y-2">
                    {dayTransactions.length > 0 ? (
                      dayTransactions.map((tx) => {
                        const account = bankAccounts.find(acc => acc.plaidAccountId === tx.account_id);
                        
                        // Determine properties based on type
                        const isRecurring = 'stream_id' in tx;
                        const key = isRecurring ? tx.stream_id : (tx as Transaction).transaction_id;
                        // Prioritize merchant_name for recurring, then description
                        const nameToDisplay = isRecurring 
                            ? (tx as RecurringTransaction).merchant_name || tx.description 
                            : (tx as Transaction).merchant_name || (tx as Transaction).name;
                        const amount = isRecurring ? tx.last_amount.amount : (tx as Transaction).amount;
                        const currency = isRecurring ? tx.last_amount.iso_currency_code : (tx as Transaction).iso_currency_code;
                        // Get logo_url if available
                        const logoUrl = isRecurring ? (tx as RecurringTransaction).logo_url : (tx as Transaction).logo_url;
                        const initialChar = (nameToDisplay || '?').charAt(0).toUpperCase();

                        return (
                          <div key={key} className="flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-2 min-w-0"> 
                              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center border overflow-hidden">
                                {logoUrl ? (
                                  <img 
                                    src={logoUrl} 
                                    alt={`${nameToDisplay || 'transaction'} logo`} 
                                    className="h-full w-full object-contain" 
                                    onError={(e) => { 
                                      // Fallback if image fails to load
                                      e.currentTarget.style.display = 'none'; 
                                      const parent = e.currentTarget.parentElement;
                                      if (parent) {
                                        const fallback = parent.querySelector('.fallback-initial');
                                        if (fallback) {
                                           (fallback as HTMLElement).style.display = 'flex';
                                        }
                                      }
                                    }} 
                                  />
                                ) : null}
                                <span 
                                  className={`fallback-initial h-full w-full items-center justify-center text-xs font-medium text-muted-foreground ${logoUrl ? 'hidden' : 'flex'}`} 
                                >
                                  {initialChar}
                                </span>
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium text-sm truncate text-foreground">{getShortName(nameToDisplay)}</span>
                                <span className="text-xs text-muted-foreground truncate">
                                  {account?.name || 'Unknown Account'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs sm:text-sm font-medium text-foreground">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: currency || 'USD',
                                }).format(amount)}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-7 w-7 text-muted-foreground hover:text-foreground" 
                                onClick={() => handleViewDetails(tx)}
                              >
                                <Eye className="h-4 w-4" />
                                <span className="sr-only">View Details</span>
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-muted-foreground italic text-sm">No purchases on this day</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Button to navigate to Subscriptions page */}
          {!isLoading && transactions.length > 0 && onNavigate && ( // Show button if there are any transactions 
            <div className="relative mt-6 mb-2 flex justify-center"> {/* Centered container */} 
              <Button 
                onClick={handleViewAll} 
                variant="default"
                className="w-full text-sm"
              >
                View All Subscriptions
              </Button>
            </div>
          )}
        </div>
      </CardContent>
      {/* Removed CardFooter content */}

      {/* Render the Transaction Detail Dialog - May need adjustments for RecurringTransaction */}
      <TransactionDetailDialog 
        transaction={viewingTransaction as Transaction} // Revert cast for now
        isOpen={!!viewingTransaction}
        onClose={handleCloseDialog}
        userCategorySettings={userCategorySettings}
        bankAccounts={bankAccounts}
      />
    </Card>
  );
}; 